/**
 * Synthesized one-shot and loop voices. Durations keep weapon cadence and
 * loop crossfades steady. Samples are generated here.
 */

const RATE = 44100;

const DUR: Record<string, number> = {
  pistol: 0.2, smg: 0.12, shotgun: 0.5, pump: 0.24, rocket: 0.8,
  railCharge: 0.4, rail: 0.7, flameStart: 0.3, flameStop: 0.3, flameLoop: 2,
  tooMuch: 0.08, droneBeep: 0.165, droneLoop: 2, gatlingSpin: 0.32, gatling: 0.08,
  gatlingStop: 0.6, droneLowBat: 0.16, droneDie: 0.75, droneDive: 0.95,
  teslaThrow: 0.28, teslaPlant: 0.16, teslaCrackle: 0.09, teslaPop: 0.22,
  meteorRush: 0.55, meteorFizz: 0.4, meteorCast: 0.22, meteorRift: 0.5, switch: 0.12, hitPaper: 0.05, hitBlock: 0.1,
  hitImage: 0.12, hitChunk: 0.06, boom: 1.3, boomSmall: 0.65, chutePop: 0.22,
  split: 0.13, blockBreak: 0.4, shatter: 0.35, fireLoop: 3, step: 0.03,
  jump: 0.14, flip: 0.2, land: 0.1, jetDeploy: 0.13, jetIgnite: 0.35, jetLoop: 2,
  jetStow: 0.25, spawn: 0.475, click: 0.04, coin: 0.12, start: 0.51, complete: 1.45,
  hitMinion: 0.07, minionDie: 0.32, roar: 0.48,
  portalOpen: 0.62, portalIn: 0.78, portalShut: 0.36,
  hurt: 0.22, heal: 0.28,
  punch: 0.09, boing: 0.26, thud: 0.14, horn: 0.34,
  drillShot: 0.16, drillGrind: 2, drillBlast: 0.55,
};

const LOOPS = new Set(["flameLoop", "droneLoop", "fireLoop", "jetLoop", "drillGrind"]);

const GAPS: Record<string, number> = {
  step: 0.09, hitPaper: 0.03, hitBlock: 0.03, pistol: 0.05, smg: 0.04, flameLoop: 0.05,
  teslaCrackle: 0.05,
  hitMinion: 0.045, roar: 0.16, coin: 0.03,
};

function clamp(n: number): number {
  return n < -1 ? -1 : n > 1 ? 1 : n;
}

function noiseAt(i: number): number {
  let x = (i + 1) * 374761393;
  x = Math.imul(x ^ (x >>> 13), 1274126177);
  return (((x ^ (x >>> 16)) >>> 0) / 4294967295) * 2 - 1;
}

function env(t: number, dur: number, attack: number, release: number): number {
  const a = attack <= 0 ? 1 : Math.min(1, t / attack);
  const r = release <= 0 ? 1 : Math.min(1, (dur - t) / release);
  return a * r;
}

function tone(t: number, freq: number, shape: "sine" | "square" | "tri" = "square"): number {
  const p = (t * freq) % 1;
  if (shape === "sine") return Math.sin(p * Math.PI * 2);
  if (shape === "tri") return p < 0.5 ? p * 4 - 1 : 3 - p * 4;
  return p < 0.5 ? 1 : -1;
}

type Voice = (t: number, dur: number) => number;

function shot(freq: number, drop: number, grit: number): Voice {
  return (t, dur) => {
    const f = freq * Math.pow(2, -drop * (t / dur));
    const n = noiseAt((t * 44100) | 0);
    return (tone(t, f, "square") * 0.55 + n * grit) * env(t, dur, 0.004, dur * 0.72);
  };
}

function boom(body: number): Voice {
  return (t, dur) => {
    const f = body * Math.pow(2, -3.2 * (t / dur));
    const n = noiseAt((t * 18000) | 0) * Math.exp(-t * 6);
    return (tone(t, f, "sine") * 0.7 + n * 0.45) * env(t, dur, 0.002, dur * 0.8);
  };
}

function loopNoise(rate: number, freq: number): Voice {
  return (t) => {
    const n = noiseAt((t * rate) | 0);
    const hum = tone(t, freq, "tri") * 0.25;
    return (n * 0.35 + hum) * 0.55;
  };
}

const VOICES: Record<string, Voice> = {
  pistol: shot(520, 1.6, 0.25),
  smg: shot(740, 1.1, 0.35),
  shotgun: (t, dur) => (noiseAt((t * 32000) | 0) * 0.8 + tone(t, 140 * Math.pow(2, -t * 4), "sine") * 0.4) * env(t, dur, 0.001, 0.28),
  pump: (t, dur) => tone(t, 180 + t * 40, "tri") * env(t, dur, 0.01, 0.12) * 0.6,
  rocket: (t, dur) => (noiseAt((t * 9000) | 0) * 0.45 + tone(t, 90 + t * 220, "saw" as "square")) * env(t, dur, 0.02, 0.2),
  railCharge: (t, dur) => tone(t, 240 + t * 900, "sine") * env(t, dur, 0.02, 0.05) * 0.7,
  rail: (t, dur) => (tone(t, 1800, "square") * 0.25 + noiseAt((t * 22000) | 0) * 0.2) * env(t, dur, 0.001, 0.45),
  flameStart: (t, dur) => noiseAt((t * 6000) | 0) * env(t, dur, 0.02, 0.12),
  flameStop: (t, dur) => noiseAt((t * 4000) | 0) * env(t, dur, 0.001, 0.2) * 0.7,
  flameLoop: loopNoise(5000, 70),
  tooMuch: shot(200, 0.4, 0.5),
  droneBeep: (t, dur) => tone(t, 880, "square") * env(t, dur, 0.005, 0.04) * 0.45,
  droneLoop: (t) => tone(t, 96, "tri") * 0.28 + tone(t, 192, "square") * 0.08,
  gatlingSpin: (t, dur) => tone(t, 80 + t * 500, "square") * env(t, dur, 0.01, 0.08) * 0.4,
  gatling: shot(980, 0.6, 0.5),
  gatlingStop: (t, dur) => tone(t, 420 * Math.pow(2, -t * 2), "tri") * env(t, dur, 0.001, 0.35) * 0.45,
  droneLowBat: (t, dur) => tone(t, t < dur * 0.5 ? 660 : 440, "square") * env(t, dur, 0.005, 0.03) * 0.4,
  droneDie: (t, dur) => (tone(t, 300 * Math.pow(2, -t * 3), "square") * 0.4 + noiseAt((t * 8000) | 0) * 0.3) * env(t, dur, 0.001, 0.4),
  droneDive: (t, dur) => noiseAt((t * 7000) | 0) * env(t, dur, 0.05, 0.15) * (0.3 + t),
  teslaThrow: (t, dur) => (tone(t, 420 * Math.pow(2, -t * 3), "square") * 0.35 + noiseAt((t * 9000) | 0) * 0.25) * env(t, dur, 0.004, 0.12),
  teslaPlant: (t, dur) => (noiseAt((t * 6000) | 0) * 0.55 + tone(t, 180, "square") * 0.3) * env(t, dur, 0.001, 0.06),
  teslaCrackle: (t, dur) => (noiseAt((t * 18000) | 0) * 0.55 + tone(t, 1680 + Math.sin(t * 90) * 220, "square") * 0.28) * env(t, dur, 0.001, 0.04),
  teslaPop: (t, dur) => (noiseAt((t * 8000) | 0) * 0.4 + tone(t, 520 * Math.pow(2, -t * 6), "sine") * 0.45) * env(t, dur, 0.002, 0.1),
  meteorRush: (t, dur) => (noiseAt((t * 5000) | 0) * 0.5 + tone(t, 90 + t * 80, "sine") * 0.25) * env(t, dur, 0.04, 0.22),
  meteorCast: (t, dur) => (noiseAt((t * 11000) | 0) * 0.35 + tone(t, 260 + t * 900, "tri") * 0.3) * env(t, dur, 0.003, 0.12),
  meteorRift: (t, dur) => (tone(t, 62 + Math.sin(t * 40) * 6, "square") * 0.22 + noiseAt((t * 2600) | 0) * 0.4) * env(t, dur, 0.03, 0.25),
  meteorFizz: (t, dur) => noiseAt((t * 9000) | 0) * env(t, dur, 0.01, 0.22) * (1 - t / dur),
  switch: (t, dur) => tone(t, 700, "square") * env(t, dur, 0.002, 0.04) * 0.35,
  hitPaper: (t, dur) => noiseAt((t * 14000) | 0) * env(t, dur, 0.001, 0.02) * 0.45,
  hitBlock: (t, dur) => (noiseAt((t * 8000) | 0) * 0.5 + tone(t, 180, "square") * 0.2) * env(t, dur, 0.001, 0.05),
  hitImage: (t, dur) => noiseAt((t * 6000) | 0) * env(t, dur, 0.001, 0.06) * 0.5,
  hitChunk: (t, dur) => noiseAt((t * 5000) | 0) * env(t, dur, 0.001, 0.03) * 0.55,
  boom: boom(70),
  boomSmall: boom(110),
  chutePop: (t, dur) => noiseAt((t * 3000) | 0) * env(t, dur, 0.001, 0.08) * 0.5,
  split: (t, dur) => tone(t, 480, "square") * env(t, dur, 0.001, 0.05) * 0.4,
  blockBreak: (t, dur) => noiseAt((t * 7000) | 0) * env(t, dur, 0.001, 0.18) * 0.55,
  shatter: (t, dur) => noiseAt((t * 12000) | 0) * env(t, dur, 0.001, 0.12) * 0.5,
  fireLoop: loopNoise(3500, 48),
  step: (t, dur) => noiseAt((t * 2000) | 0) * env(t, dur, 0.001, 0.012) * 0.35,
  jump: (t, dur) => tone(t, 280 + t * 200, "square") * env(t, dur, 0.002, 0.06) * 0.35,
  flip: (t, dur) => tone(t, 420, "square") * env(t, dur, 0.002, 0.08) * 0.3,
  land: (t, dur) => noiseAt((t * 2500) | 0) * env(t, dur, 0.001, 0.04) * 0.45,
  jetDeploy: (t, dur) => noiseAt((t * 5000) | 0) * env(t, dur, 0.005, 0.04) * 0.4,
  jetIgnite: (t, dur) => noiseAt((t * 8000) | 0) * env(t, dur, 0.02, 0.1),
  jetLoop: (t) => noiseAt((t * 6000) | 0) * 0.22 + tone(t, 62, "tri") * 0.18,
  jetStow: (t, dur) => tone(t, 200 * Math.pow(2, -t * 3), "tri") * env(t, dur, 0.001, 0.1) * 0.35,
  spawn: (t, dur) => tone(t, 330 + t * 220, "sine") * env(t, dur, 0.02, 0.15) * 0.45,
  click: (t, dur) => tone(t, 1200, "square") * env(t, dur, 0.001, 0.015) * 0.3,
  coin: (t, dur) => tone(t, t < dur * 0.45 ? 988 : 1319, "square") * env(t, dur, 0.001, 0.025) * 0.34,
  hitMinion: (t, dur) => (noiseAt((t * 9000) | 0) * 0.5 + tone(t, 210, "square") * 0.28) * env(t, dur, 0.001, 0.028),
  minionDie: (t, dur) => (tone(t, 196 * Math.pow(2, -t * 7), "square") * 0.4 + noiseAt((t * 6400) | 0) * 0.45) * env(t, dur, 0.001, 0.14),
  roar: (t, dur) => (tone(t, 68 + Math.sin(t * 28) * 16, "square") * 0.4 + noiseAt((t * 3800) | 0) * 0.28) * env(t, dur, 0.02, 0.22),
  start: (t, dur) => tone(t, 220 + t * 180, "square") * env(t, dur, 0.01, 0.2) * 0.4,
  complete: (t, dur) => (tone(t, 523, "sine") * (t < 0.4 ? 1 : 0) + tone(t, 659, "sine") * (t > 0.18 && t < 0.7 ? 1 : 0) + tone(t, 784, "sine") * (t > 0.36 ? 1 : 0)) * env(t, dur, 0.01, 0.4) * 0.35,
  portalOpen: (t, dur) => {
    const f = 240 + t * 720;
    const n = noiseAt((t * 7000) | 0) * 0.12;
    return (tone(t, f, "sine") * 0.42 + tone(t, f * 1.5, "tri") * 0.22 + n) * env(t, dur, 0.02, 0.28);
  },
  portalIn: (t, dur) => {
    const f = 860 * Math.pow(2, -2.4 * (t / dur));
    return (tone(t, f, "sine") * 0.48 + tone(t, f * 0.5, "tri") * 0.22 + noiseAt((t * 5000) | 0) * 0.08) * env(t, dur, 0.01, 0.32);
  },
  portalShut: (t, dur) => (tone(t, 520 * Math.pow(2, -t * 4), "square") * 0.28 + noiseAt((t * 4000) | 0) * 0.2) * env(t, dur, 0.005, 0.16),
  hurt: (t, dur) => (tone(t, 310 * Math.pow(2, -t * 6), "square") * 0.4 + noiseAt((t * 6000) | 0) * 0.22) * env(t, dur, 0.004, 0.1),
  heal: (t, dur) => (tone(t, t < dur * 0.45 ? 660 : 880, "sine") * 0.34) * env(t, dur, 0.008, 0.08),
  punch: (t, dur) => {
    const n = noiseAt((t * 18000) | 0);
    const rise = tone(t, 180 + t * 2400, "square");
    return (n * 0.55 + rise * 0.28) * env(t, dur, 0.002, 0.035);
  },
  boing: (t, dur) => {
    const f = 520 * Math.pow(2, -1.6 * (t / dur));
    return (tone(t, f, "sine") * 0.55 + tone(t, f * 2, "sine") * 0.16 + tone(t, f * 1.5, "tri") * 0.12) * env(t, dur, 0.004, 0.1);
  },
  thud: (t, dur) => {
    const f = 78 * Math.pow(2, -2.2 * (t / dur));
    const n = noiseAt((t * 6000) | 0) * Math.exp(-t * 18);
    return (tone(t, f, "sine") * 0.7 + n * 0.4) * env(t, dur, 0.001, 0.05);
  },
  horn: (t, dur) => {
    const f = 280 + Math.sin(t * 46) * 70 + t * 520;
    return tone(t, f, "square") * 0.28 * env(t, dur, 0.008, 0.12);
  },
  drillShot: (t, dur) => {
    const metal = tone(t, 680 * Math.pow(2, -t * 9), "square");
    const n = noiseAt((t * 18000) | 0);
    return (metal * 0.34 + n * 0.55) * env(t, dur, 0.001, 0.07);
  },
  drillGrind: (t) => {
    const n = noiseAt((t * 9000) | 0);
    const buzz = tone(t, 86 + Math.sin(t * 46) * 28, "square") * 0.2;
    const grit = noiseAt((t * 2200) | 0) * 0.38;
    return (n * 0.26 + buzz + grit) * 0.62;
  },
  drillBlast: (t, dur) => {
    const crack = noiseAt((t * 16000) | 0) * Math.exp(-t * 18);
    const gravel = noiseAt((t * 4200) | 0) * Math.exp(-t * 3.5);
    const thump = tone(t, 78 * Math.pow(2, -t * 2.4), "tri") * Math.exp(-t * 5);
    return (crack * 0.5 + gravel * 0.38 + thump * 0.46) * env(t, dur, 0.001, 0.32);
  },
};

export class AudioBus {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private output: AudioNode | null = null;
  private buffers = new Map<string, AudioBuffer[]>();
  private last = new Map<string, number>();
  private loops = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();
  volume = 1;
  ready = false;

  async load() {
    try {
      const ctx = new AudioContext();
      for (const [id, dur] of Object.entries(DUR)) {
        const voice = VOICES[id];
        if (!voice) continue;
        this.buffers.set(id, [this.render(ctx, id, dur, voice)]);
      }
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -10;
      comp.knee.value = 8;
      comp.ratio.value = 4;
      comp.attack.value = 0.003;
      comp.release.value = 0.2;
      const master = ctx.createGain();
      master.gain.value = this.volume * this.volume;
      comp.connect(master);
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      this.output = comp;
      this.ready = true;
    } catch (err) {
      console.warn("[destroy-any-website] audio unavailable", err);
    }
  }

  private render(ctx: AudioContext, id: string, dur: number, voice: Voice): AudioBuffer {
    const n = Math.max(1, Math.round(dur * RATE));
    const buf = ctx.createBuffer(1, n, RATE);
    const data = buf.getChannelData(0);
    for (let i = 0; i < n; i++) data[i] = clamp(voice(i / RATE, dur));
    if (LOOPS.has(id)) {
      const fade = Math.min(256, n >> 2);
      for (let i = 0; i < fade; i++) {
        const k = i / fade;
        data[i] = data[i] * k + data[n - fade + i] * (1 - k);
      }
    }
    return buf;
  }

  unlock() {
    if (this.ctx?.state === "suspended") void this.ctx.resume();
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this.volume * this.volume;
    try {
      localStorage.setItem("daw:volume", String(this.volume));
    } catch {
      /* private mode */
    }
  }

  play(id: string, pitch = 1, gain = 1) {
    if (!this.ctx || !this.output || this.volume <= 0) return;
    const now = this.ctx.currentTime;
    const gap = GAPS[id] ?? 0.02;
    if (now - (this.last.get(id) ?? -1) < gap) return;
    this.last.set(id, now);
    const list = this.buffers.get(id);
    if (!list?.length) return;
    const buf = list[Math.floor(Math.random() * list.length)];
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = pitch * (1 + (Math.random() - 0.5) * 0.04);
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(g);
    g.connect(this.output);
    src.start();
  }

  loop(id: string, on: boolean, gain = 0.6) {
    if (!this.ctx || !this.output) return;
    const cur = this.loops.get(id);
    if (!on) {
      if (cur) {
        cur.gain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
        const src = cur.src;
        setTimeout(() => { try { src.stop(); } catch { /* already */ } }, 200);
        this.loops.delete(id);
      }
      return;
    }
    if (cur) return;
    const list = this.buffers.get(id);
    if (!list?.length) return;
    const src = this.ctx.createBufferSource();
    src.buffer = list[0];
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.value = gain * this.volume;
    src.connect(g);
    g.connect(this.output);
    src.start();
    this.loops.set(id, { src, gain: g });
  }
}
